import React, { useMemo, useState } from "react";

// Styling mirrors the energy Live table (channelStyles in LiveTab.js) so both look identical.
const styles = {
  table: { width: "100%", borderCollapse: "collapse", fontSize: "14px" },
  th: {
    padding: "12px 8px",
    textAlign: "center",
    borderBottom: "2px solid #dee2e6",
    backgroundColor: "#f8f9fa",
    fontWeight: 600,
    whiteSpace: "nowrap",
  },
  tr: { transition: "background-color 0.2s" },
  td: {
    padding: "10px 8px",
    textAlign: "center",
    borderBottom: "1px solid #dee2e6",
  },
  message: { padding: "32px", textAlign: "center", color: "#64748b" },
};

const StatusBadge = ({ value, type }) => {
  const color = (() => {
    if (type === "status") return value === "ON" ? "#90EE90" : "#FA8072";
    if (value === "online") return "#90EE90";
    if (value === "idle") return "#FFD700";
    if (value === "offline") return "#FA8072";
    return "transparent";
  })();

  if (!value || value === "-") return <span>-</span>;

  return (
    <span
      style={{
        backgroundColor: color,
        borderRadius: "4px",
        padding: "2px 12px",
        display: "inline-block",
        fontWeight: 500,
        cursor: "default",
      }}
    >
      {value}
    </span>
  );
};

// Same column shape as the energy table, with environment metrics in the middle.
const COLUMNS = [
  { key: "machine_name", label: "Name", align: "left" },
  { key: "timestamp", label: "Time" },
  { key: "temperature", label: "Temperature (°C)" },
  { key: "humidity", label: "Humidity (%)" },
  { key: "avg_temperature", label: "Avg Temperature (°C)" },
  { key: "avg_humidity", label: "Avg Humidity (%)" },
  { key: "status", label: "Status" },
  { key: "meter_status", label: "Meter Status" },
];

const formatNumber = (value) =>
  value === null || value === undefined || Number.isNaN(Number(value))
    ? "-"
    : Number(value).toFixed(1);

// A sensor with no data for >30 min comes back stale; render it like the energy table does.
const isRowStale = (row) =>
  row.is_stale || !row.timestamp || row.timestamp === "-" || row.meter_status === "offline";

const EnvironmentLiveTable = ({ data = [], loading = false, error = null }) => {
  const [sortColumn, setSortColumn] = useState(null);
  const [sortDirection, setSortDirection] = useState("asc");

  const handleSort = (column) => {
    if (sortColumn === column) {
      setSortDirection((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortColumn(column);
      setSortDirection("asc");
    }
  };

  const rows = useMemo(() => {
    const list = [...data];
    if (!sortColumn) return list;
    const dir = sortDirection === "asc" ? 1 : -1;
    return list.sort((a, b) => {
      const av = a[sortColumn];
      const bv = b[sortColumn];
      const aEmpty = av === null || av === undefined || av === "" || av === "-";
      const bEmpty = bv === null || bv === undefined || bv === "" || bv === "-";
      if (aEmpty && bEmpty) return 0;
      if (aEmpty) return 1; // missing values always sink to the bottom
      if (bEmpty) return -1;
      if (typeof av === "number" && typeof bv === "number") return (av - bv) * dir;
      return String(av).localeCompare(String(bv)) * dir;
    });
  }, [data, sortColumn, sortDirection]);

  if (loading && data.length === 0) {
    return <div style={styles.message}>Loading sensors...</div>;
  }
  if (error) {
    return <div style={{ ...styles.message, color: "#dc2626" }}>{error}</div>;
  }
  if (data.length === 0) {
    return <div style={styles.message}>No sensors found</div>;
  }

  return (
    <div>
      <div style={{ overflowX: "auto" }}>
        <table style={styles.table}>
          <thead>
            <tr>
              {COLUMNS.map((col) => (
                <th
                  key={col.key}
                  style={{
                    ...styles.th,
                    textAlign: col.align || "center",
                    cursor: "pointer",
                    userSelect: "none",
                  }}
                  onClick={() => handleSort(col.key)}
                >
                  {col.label}
                  <span style={{ marginLeft: 4, opacity: sortColumn === col.key ? 1 : 0.3 }}>
                    {sortColumn === col.key ? (sortDirection === "asc" ? "↑" : "↓") : "↕"}
                  </span>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((row, idx) => {
              const stale = isRowStale(row);
              return (
                <tr
                  key={row.meter_id || `${row.machine_name}-${idx}`}
                  style={{
                    ...styles.tr,
                    backgroundColor: idx % 2 === 1 ? "#f8f9fa" : "#fff",
                  }}
                >
                  <td
                    style={{
                      ...styles.td,
                      textAlign: "left",
                      paddingLeft: 8,
                      fontWeight: 600,
                    }}
                  >
                    {row.machine_name}
                  </td>
                  <td style={{ ...styles.td, color: stale ? "#dc2626" : undefined }}>
                    {stale ? "More than 30 mins" : row.timestamp}
                  </td>
                  <td style={styles.td}>{stale ? "-" : formatNumber(row.temperature)}</td>
                  <td style={styles.td}>{stale ? "-" : formatNumber(row.humidity)}</td>
                  <td style={styles.td}>
                    {stale ? "-" : formatNumber(row.avg_temperature)}
                  </td>
                  <td style={styles.td}>{stale ? "-" : formatNumber(row.avg_humidity)}</td>
                  <td style={styles.td}>
                    <StatusBadge value={row.status || "-"} type="status" />
                  </td>
                  <td style={styles.td}>
                    <StatusBadge value={row.meter_status || "-"} type="meterStatus" />
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default EnvironmentLiveTable;
